<template>
  <section class="position-relative pt-4 select-items px-3" v-if="!isLoading && !initialLoading">
    <b-row class="justify-content-md-center">
      <b-col lg="10" class="pl-sm-3 pr-sm-3 pl-0 pr-0">
        <div v-if="orders.length">
          <div class="orders-container" v-for="order in orders" :key="'order-' + order.id.toString()">
            <div class="card-header">
              <div class="top-container d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <span class="top-container-text order_number">#{{order.order_id}}</span>
                  <span class="top-container-text order_date">
                    <span class="separator">|</span>
                    {{getShortHumanReadableDate(order.created)}}
                  </span>
                  <span class="separator">|</span>
                  <span class="badge order-status payment_status active" :class="getPaymentStatusClass(order)">{{getPaymentStatus(order)}}</span>
                </div>
              </div>
            </div>
            <div class="order-container">
              <div
                :class="{'clickable-order-container': !useIntentButtons}"
                v-for="item in order.items"
                :key="'item-' + item.id.toString()"
                @click="selectItemArea($event, item)"
              >
                <div class="d-block d-md-none">
                  <template v-if="item.campaign_product.show_next_bill_price">
                    <template v-if="isSubscriptionActive(item)">
                      <div class="item-img-top-text" v-if="item.next_bill_date">
                        Next billing date : {{getShortHumanReadableUTCDate(item.next_bill_date)}}
                      </div>
                      <div class="item-img-top-text" v-if="isSubscriptionPauseScheduled(item)">
                        Paused until {{getShortHumanReadableUTCDate(item.pause_resume_date)}}
                      </div>
                    </template>
                    <div class="item-img-top-text" v-else-if="isSubscriptionCancelScheduled(item)">
                      Cancel on {{getShortHumanReadableUTCDate(item.cancel_date)}}
                    </div>
                    <div class="item-img-top-text" v-else-if="isSubscriptionComplete(item)">
                      No future billing
                    </div>
                  </template>
                </div>
                <div class="order-item-container pl-0" :class="{selected: isSelected(item)}">
                  <div class="item-image">
                    <img :src="getImageUrl(item)" @error="setAltImg($event, item)" alt="" />
                  </div>
                  <div class="order-item-info">
                    <div class="item-detail-text">
                      <span class="item-name" v-html="item.name"></span>
                      <div v-if="item.variant" class="item-variants">
                        <div v-for="option in item.variant.options" :key="'variant-option-' + option.id">
                          <span v-if="!option.is_deleted">{{option.name}}: {{option.value}}</span>
                        </div>
                      </div>
                    </div>
                    <div class="notranslate item-detail-text">
                      <div :class="{'original-price': isReplaced(item)}">
                        <span v-if="item.quantity > 1 || showFulfillment(item)">{{item.quantity}} x </span>{{item.price | currency(order.currency)}}
                      </div>
                    </div>
                    <div class="d-none d-md-block">
                      <template v-if="isRecurring(item)">
                        <div class="item-detail-text">
                          <span class="mr-2">Subscription</span>
                          <span class="badge order-status" :class="getItemStatusClass(item)">{{getItemStatus(item)}}</span>
                        </div>
                        <template v-if="item.campaign_product.show_next_bill_price">
                          <template v-if="isSubscriptionActive(item)">
                            <div class="item-detail-text" v-if="item.next_bill_date">
                              Next billing date : {{getShortHumanReadableUTCDate(item.next_bill_date)}}
                            </div>
                            <div class="item-detail-text" v-if="isSubscriptionPauseScheduled(item)">
                              Paused until {{getShortHumanReadableUTCDate(item.pause_resume_date)}}
                            </div>
                          </template>
                          <div class="item-detail-text" v-else-if="isSubscriptionCancelScheduled(item)">
                            Cancel on {{getShortHumanReadableUTCDate(item.cancel_date)}}
                          </div>
                          <div class="item-detail-text" v-else-if="isSubscriptionComplete(item)">
                            No future billing
                          </div>
                        </template>
                      </template>
                      <template v-else>
                        <div class="item-detail-text">One-time purchase</div>
                      </template>
                    </div>
                    <div class="item-detail-text" v-if="hasPendingCharge(item)">
                      Pending charge for {{item.amount_pending | currency(order.currency)}}
                    </div>
                    <div class="item-detail-text" v-if="showFulfillment(item)">
                      <span class="mr-2">Shipment</span>
                      <span class="badge order-status" :class="getItemFulfillmentStatusClass(item)">{{getItemFulfillmentStatus(item)}}</span>
                    </div>
                  </div>
                  <div class="d-none d-md-block item-select-btn-container">
                    <SelectItemButtons
                      :selected="isSelected(item)"
                      :item-select-label="itemSelectLabel"
                      :item-select-icon="itemSelectIcon"
                      :use-intents="useIntentButtons"
                      :intents="item['actions']"
                      @select="selectItemButton(item, ...arguments)"
                    ></SelectItemButtons>
                  </div>
                </div>
                <div v-if="!noselect" class="d-block d-md-none order-item-container p-0">
                  <hr class="m-0"/>
                  <div class="card-padding item-select-btn-container">
                    <SelectItemButtons
                      :selected="isSelected(item)"
                      :item-select-label="itemSelectLabel"
                      :item-select-icon="itemSelectIcon"
                      :use-intents="useIntentButtons"
                      :intents="item['actions']"
                      @select="selectItemButton(item, ...arguments)"
                    ></SelectItemButtons>
                  </div>
                </div>
                <div class="d-none campaign-product-id">{{item.campaign_product.campaign_product_id}}</div>
                <hr />
              </div>
            </div>
            <div class="order-totals-info d-flex">
              <div>
                <div class="order-totals-info-item">
                  <span class="total-heading">Subtotal:</span>
                  <span class="notranslate total-text">{{order.subtotal | currency(order.currency)}}</span>
                </div>
                <div class="order-totals-info-item" v-if="showShipping(order)">
                  <span class="total-heading">Shipping:</span>
                  <span class="notranslate total-text">{{order.shipping_price | currency(order.currency)}}</span>
                </div>
                <div class="order-totals-info-item" v-if="showHiddenFees(order)">
                  <span class="total-heading">{{session.hidden_fees_label}}:</span>
                  <span class="notranslate total-text">{{order.hidden_fees | currency(order.currency)}}</span>
                </div>
                <div class="order-totals-info-item" v-if="showDiscount(order)">
                  <span class="total-heading">Discount:</span>
                  <span class="notranslate total-text cancelled">-{{order.discount | currency(order.currency)}}</span>
                </div>
                <div class="order-totals-info-item" v-if="Number(order.tax) > 0">
                  <span class="total-heading">Tax:</span>
                  <span class="notranslate total-text">{{order.tax | currency(order.currency)}}</span>
                </div>
                <div class="order-totals-info-item" v-if="Number(order.tip) > 0">
                  <span class="total-heading">Tip:</span>
                  <span class="notranslate total-text">{{order.tip | currency(order.currency)}}</span>
                </div>
                <hr class="mt-0 order-totals-info-item w-100" />
                <div class="order-totals-info-item" >
                  <div class="label total-heading fw-500">Total:</div>
                  <div class="notranslate total-text">{{order.total | currency(order.currency)}}</div>
                </div>
              </div>
              <div>
                <div class="order-totals-info-item" v-if="showPastBalance(order)">
                  <div class="total-heading">Past Payments:</div>
                  <div class="notranslate total-text">{{order.past_balance | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item" v-if="showForwardBalance(order)">
                  <div class="total-heading">Carry Forward:</div>
                  <div class="notranslate total-text cancelled">-{{order.forward_balance | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item">
                  <div class="total-heading">Amount Paid:</div>
                  <div class="notranslate total-text">{{order.paid | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item" v-if="showStoreDebit(order)">
                  <div class="total-heading">Credited:</div>
                  <div class="notranslate total-text">{{order.store_debit | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item" v-if="showRefund(order)">
                  <div class="total-heading">Refunded:</div>
                  <div class="notranslate total-text cancelled">-{{order.refund | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item" v-if="showPendingRefund(order)">
                  <div class="total-heading">
                    <b-tooltip target="help" triggers="hover" placement="top">
                      The refund is pending because the payment has not been captured yet.<br/>
                      We will be able to process the refund once the charge is captured.<br/>
                      This may take a few business days to appear.<br/>
                    </b-tooltip>
                    Pending Refund: <img id="help" :src="require('@/assets/images/help.svg')"/>
                  </div>
                  <div class="notranslate total-text pending">-{{order.pending_refund | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item" v-if="showStoreCredit(order)">
                  <div class="total-heading">Store Credit:</div>
                  <div class="notranslate total-text cancelled">-{{order.store_credit | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item" v-if="showChargeback(order)">
                  <div class="total-heading">Chargeback:</div>
                  <div class="notranslate total-text cancelled">-{{order.chargeback | currency(order.currency)}}</div>
                </div>
                <hr class="mt-0 order-totals-info-item w-100" />
                <div class="order-totals-info-item" >
                  <div class="label total-heading fw-500">Total Paid:</div>
                  <div class="notranslate total-text">{{order.total_remaining_balance | currency(order.currency)}}</div>
                </div>
                <div class="order-totals-info-item" v-if="showAmountDue(order)">
                  <div class="label total-heading fw-500">Amount Due:</div>
                  <div class="notranslate total-text">{{order.amount_due | currency(order.currency)}}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="centered-block">
            <div class="step-title">No items found</div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row class="justify-content-center boxes select-item-btn mt-2 ml-sm-0 mr-sm-0 m-w-initial">
      <b-col v-if="session && session.can_undo && !noback" md="4" lg="3" :cols="multiple ? '6': '12'" class="back p-0">
        <button type="button" @click="goBack()" class="btn-base new-btn-outline-success input-choice-btn stroke-2x">Back</button>
      </b-col>
      <b-col v-if="multiple" md="4" lg="3" cols="6" class="p-0">
        <button type="button" @click="done()" class="btn-base input-choice-btn font-weight-normal stroke-2x" :disabled="!canContinue">{{label}}
          <font-awesome-icon :icon="['fas', 'caret-right']" class="footer_btn"></font-awesome-icon>
        </button>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import axios from 'axios'
import {mapState} from 'vuex'
import {
  sortItems, getShortDate, getUTCShortDate, getItemStatus, getItemStatusClass, getItemImageUrl,
  getItemFulfillmentStatus, getItemFulfillmentStatusClass, showSubscription, showFulfillment,
  getShortHumanReadableDate, getPaymentStatus, getPaymentStatusClass, getShortHumanReadableUTCDate,
  isRecurring, setFallbackImageUrl, getItemIntents
} from '@/utils'
import {BillingCycleType, ItemStatus, InputType} from '@/constants'
const SelectItemButtons = () => import('./SelectItemButtons')

export default {
  name: 'SelectItems',
  components: {SelectItemButtons},
  props: {
    input: Object,
    label: String,
    defaults: {
      type: Array,
      default: function() {
        return []
      }
    },
    noback: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    noselect: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    multiple: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    itemSelectLabel: {
      type: String,
      default: function() {
        return 'Select'
      }
    },
    itemSelectIcon: {
      type: String,
      default: function() {
        return 'angle-right'
      }
    }
  },
  data: function() {
    return {
      getShortDate,
      getUTCShortDate,
      orders: [],
      selectedItemMap: {},
      canContinue: false,
      getShortHumanReadableDate,
      getShortHumanReadableUTCDate,
      initialLoading: true
    }
  },
  computed: {
    ...mapState(['session', 'isLoading', 'customerPortalSettings']),
    activeItemRequired: function() {
      return this.input && this.input.requires_active_item
    },
    isCheckout: function() {
      return this.input && (this.input.type === InputType.CheckoutConfirm)
    },
    useIntentButtons: function() {
      return !this.multiple && this.session && this.session.use_intents
    }
  },
  methods: {
    done: function() {
      if (this.canContinue) {
        let selectedItems = []

        this.orders.forEach(order => {
          order.items.forEach(item => {
            if (item.id in this.selectedItemMap) {
              selectedItems.push(item)
            }
          })
        })

        this.$emit('select', selectedItems) // done selecting items
      }
    },
    goBack: function() {
      this.$emit('back') // go back to the previous step (ignore changes to item selection)
    },
    showFulfillment: function(item) {
      return showFulfillment(item)
    },
    showSubscription: function(item) {
      return showSubscription(item)
    },
    isRecurring: function(item) {
      return isRecurring(item)
    },
    isSubscriptionActive: function(item) {
      return (item.billing_cycle_type === BillingCycleType.Recurring) && item.can_cancel_subscription
    },
    isSubscriptionPauseScheduled: function(item) {
      return this.isSubscriptionActive(item) && (item.status !== ItemStatus.Paused) && item.pause_resume_date
    },
    isSubscriptionCancelScheduled: function(item) {
      return item.cancel_date
    },
    isSubscriptionComplete: function(item) {
      return item.status === ItemStatus.Complete
    },
    isReplaced: function(item) {
      return item.status === ItemStatus.Replaced
    },
    getPaymentStatus: function(order) {
      return getPaymentStatus(order, false)
    },
    getPaymentStatusClass: function(order) {
      return getPaymentStatusClass(order, false)
    },
    getItemStatus: function(item) {
      return getItemStatus(item, false)
    },
    getItemStatusClass: function(item) {
      return getItemStatusClass(item)
    },
    getItemFulfillmentStatus: function(item) {
      return getItemFulfillmentStatus(item)
    },
    getItemFulfillmentStatusClass: function(item) {
      return getItemFulfillmentStatusClass(item)
    },
    hasPendingCharge: function(item) {
      return parseFloat(item.amount_pending) > Number.EPSILON
    },
    showRefund: function(order) {
      return parseFloat(order.refund) > Number.EPSILON
    },
    showPendingRefund: function(order) {
      return parseFloat(order.pending_refund) > Number.EPSILON
    },
    showChargeback: function(order) {
      return parseFloat(order.chargeback) > Number.EPSILON
    },
    showAmountDue: function(order) {
      return parseFloat(order.amount_due) > Number.EPSILON
    },
    showDiscount: function(order) {
      return parseFloat(order.discount) > Number.EPSILON
    },
    showHiddenFees: function(order) {
      return parseFloat(order.hidden_fees) > Number.EPSILON
    },
    showShipping: function(order) {
      return (parseFloat(order.shipping_price) > Number.EPSILON) || !this.showHiddenFees(order)
    },
    showForwardBalance: function(order) {
      return parseFloat(order.forward_balance) > Number.EPSILON
    },
    showPastBalance: function(order) {
      return parseFloat(order.past_balance) > Number.EPSILON
    },
    showStoreCredit: function(order) {
      return parseFloat(order.store_credit) > Number.EPSILON
    },
    showStoreDebit: function(order) {
      return parseFloat(order.store_debit) > Number.EPSILON
    },
    selectItemArea: function(event, item) {
      event.stopPropagation()

      if (!this.useIntentButtons) {
        this.selectItemButton(item)
      }
    },
    selectItemButton: function(item, intent) {
      if (this.multiple) {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        } else {
          this.selectedItemMap[item.id] = true
        }

        this.updateStatus()
      } else {
        this.$emit('select', [item], intent) // single selection - done selecting items
      }
    },
    isSelected: function(item) {
      return this.selectedItemMap && !!(item.id in this.selectedItemMap)
    },
    selectAll: function() {
      this.orders.forEach(order => {
        this.selectOrder(order)
      })

      this.updateStatus()
    },
    clearAll: function() {
      this.selectedItemMap = {}
      this.updateStatus()
    },
    selectOrder: function(order) {
      order.items.forEach(item => {
        this.selectedItemMap[item.id] = true
      })

      this.updateStatus()
    },
    clearOrder: function(order) {
      order.items.forEach(item => {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        }
      })

      this.updateStatus()
    },
    getImageUrl: function(item) {
      return getItemImageUrl(item, 'medium')
    },
    setAltImg: function(event, item) {
      setFallbackImageUrl(event, item, 'medium', true)
    },
    updateStatus: function() {
      // update the select all checkbox
      let selectionCount = Object.keys(this.selectedItemMap).length
      this.canContinue = selectionCount !== 0

      this.orders = this.orders.slice() // force item statuses to be called
    }
  },
  created() {
    this.$store.commit('busy')
    this.selectedItemMap = {}
    const sessionIntents = new Set(this.session ? this.session.intents : [])

    // initialize the set of default selected items
    if (this.multiple) {
      this.defaults.forEach(item => {
        this.selectedItemMap[item.id] = true
      })
    }

    axios.get('/order_items/', {
      params: {
        is_active: this.activeItemRequired,
        checkout: this.isCheckout
      }
    }).then(result => {
      let items = result.data.results
      let lastOrderId = null
      let orders = []
      let order = {}
      let startIndex = 0
      const settings = this.customerPortalSettings || {}

      sortItems(items)

      for (let i = 0; i < items.length; i++) {
        let orderId = items[i].order.id
        console.log(items[i])

        if (this.useIntentButtons) {
          items[i]['actions'] = getItemIntents(items[i], settings).filter(intent => sessionIntents.has(intent))
        }

        if (orderId !== lastOrderId) { // if this is the 1st item of a different order
          if (i > 0) {
            // if this is not the 1st item, then copy the items from the previous order
            order['items'] = items.slice(startIndex, i)
            orders.push(order)
          }

          lastOrderId = orderId
          startIndex = i
          order = {
            id: items[i].order.id,
            order_id: items[i].order.display_order_id || items[i].order.order_id,
            created: items[i].order.created,
            subtotal: items[i].order.subtotal,
            shipping_price: items[i].order.shipping_price,
            hidden_fees: items[i].order.hidden_fees,
            tax: items[i].order.tax,
            charged: items[i].order.amount_charged,
            paid: items[i].order.amount_paid,
            discount: items[i].order.discount,
            total: items[i].order.total_discounted_price,
            total_remaining_balance: items[i].order.total_remaining_balance,
            amount_due: items[i].order.amount_due,
            pending_refund: items[i].order.pending_refund_amount,
            refund: items[i].order.settled_refund_amount,
            chargeback: items[i].order.chargeback_amount,
            forward_balance: items[i].order.forward_balance,
            past_balance: items[i].order.past_balance,
            status: items[i].order.status,
            payment_status: items[i].order.payment_status,
            tip: items[i].order.tip,
            currency: items[i].order.currency,
            store_credit: items[i].order.store_credit,
            store_debit: items[i].order.store_debit
          }
        }

        if (i === (items.length - 1)) { // if this is the last item
          order['items'] = items.slice(startIndex)
          orders.push(order)
        }
      }

      this.orders = orders
      this.updateStatus()
      this.$store.commit('ready')

      if (!items.length) {
        this.canContinue = true
      }
      this.initialLoading = false
    }, error => {
      console.error(error)
      this.$store.commit('critical_error', error)
      this.$store.commit('ready')
      this.initialLoading = false
    })
  }
}
</script>

<style scoped>
</style>
